import {
  readFileBytes,
  setupMarkdownToolPage,
} from './shared/index.js';
import {
  PDF_MAX_BYTES,
  validatePdfBytes,
} from './formats/pdf/convert.js';

function runWorker(bytes, options, signal, report) {
  if (typeof Worker === 'undefined') return Promise.reject(new Error('This browser cannot start the local PDF conversion worker.'));
  if (signal?.aborted) return Promise.reject(new DOMException('Conversion canceled.', 'AbortError'));
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./formats/pdf/worker.js', import.meta.url), { type: 'module', name: 'kivoza-pdf-to-markdown' });
    let settled = false;
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', cancel);
      worker.terminate();
      callback(value);
    };
    const cancel = () => finish(reject, new DOMException('Conversion canceled.', 'AbortError'));
    signal?.addEventListener('abort', cancel, { once: true });
    worker.onmessage = (event) => {
      const message = event.data || {};
      if (message.type === 'progress') {
        report(message.message, message.progress);
        return;
      }
      if (message.type === 'result') finish(resolve, message.result);
      else if (message.type === 'error') finish(reject, new Error(message.error || 'PDF conversion failed.'));
    };
    worker.onerror = (event) => finish(reject, new Error(event.message || 'PDF conversion worker failed.'));
    // Transfer a copy so the selected source remains available for retry.
    const transferable = bytes.slice();
    worker.postMessage({ bytes: transferable, options }, [transferable.buffer]);
  });
}

export function initPdfPage(root = document) {
  const pageRange = root.querySelector('#page-range');
  const headingMode = root.querySelector('#pdf-heading-mode');
  const includePageBreaks = root.querySelector('#pdf-include-page-breaks');
  let sourceBytes = null;
  let sourceFile = null;
  let reading = false;
  const page = setupMarkdownToolPage({
    root,
    defaultFileName: 'pdf-document',
    onFiles: async (files) => {
      const file = files?.[0];
      if (!file) return;
      if (files.length > 1) {
        page.setStatus('Choose one PDF file at a time.', 'error');
        return;
      }
      if (reading) return;
      page.setReady(false);
      reading = true;
      try {
        const bytes = await readFileBytes(file, PDF_MAX_BYTES, 'PDF file');
        const problem = validatePdfBytes(bytes);
        if (problem) throw new Error(problem);
        sourceBytes = bytes;
        sourceFile = file;
        page.setReady(true);
        page.setStatus(`${file.name} is ready. Choose pages, then convert locally.`, 'success');
      } catch (error) {
        sourceBytes = null;
        sourceFile = null;
        page.setStatus(error instanceof Error ? error.message : 'The PDF file could not be read.', 'error');
      } finally {
        reading = false;
      }
    },
  });
  if (page.elements.processingBoundary) {
    page.elements.processingBoundary.textContent = 'Processing boundary: PDF bytes stay in this browser. No upload or remote conversion request is made. OCR is not performed; scanned pages are reported for review.';
  }
  page.elements.convertButton?.addEventListener('click', () => {
    if (!sourceBytes) {
      page.setStatus('Choose a PDF file first.', 'error');
      return;
    }
    const options = {
      pageRange: pageRange?.value || '',
      headingMode: headingMode?.value || 'preserve',
      includePageBreaks: includePageBreaks ? Boolean(includePageBreaks.checked) : true,
    };
    page.runConversion(async ({ signal, report }) => {
      report('Preparing the local PDF worker…', 0.05);
      return runWorker(sourceBytes, options, signal, report);
    }).catch(() => {});
  });
  page.elements.clearButton?.addEventListener('click', () => {
    sourceBytes = null;
    sourceFile = null;
    if (pageRange) pageRange.value = '';
    page.setReady(false);
    page.result.clear();
    page.setStatus('Source cleared.');
  });
  page.setReady(false);
  return {
    page,
    get sourceFile() { return sourceFile; },
    get sourceBytes() { return sourceBytes; },
  };
}

if (typeof document !== 'undefined' && document.documentElement.dataset.markdownFormat === 'pdf') initPdfPage();
